import { observer } from 'mobx-react';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Text } from '@servicetitan/anvil2';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { App as WFApp } from '@servicetitan/web-feature-app-service';

import { TopicPickerSubHeader } from './topic-picker-sub-header';
import { TopicsViews } from './topics-view';
import { TopicType } from '../../common/api/topics.api';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { ErrorView } from '../../common/components/error-view';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { MessageRow } from '../../common/components/message-row/message-row';
import { YesNoButton } from '../../common/components/yes-no-button/yes-no-button';
import { ErrorModel } from '../../common/models/error.model';
import { MessageType } from '../../common/models/message-type.model';
import { TopicViewModel } from '../../common/models/topic.model';
import { AppSettingsStore } from '../../common/stores/app-settings.store';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { MainStore } from '../../common/stores/main.store';
import { navigateToClose } from '../../common/utils/navigate-to-close';
import { TopicPickerStore } from '../stores/topic-picker.store';

export const TopicPickerPage = provide({ singletons: [TopicPickerStore] })(
    observer(() => {
        const [topicPickerStore, chatStore, mainStore, { setFmaAppVersion, openedFromCordova }] =
            useDependencies<[TopicPickerStore, ChatStore, MainStore, AppSettingsStore]>(
                TopicPickerStore,
                ChatStore,
                MainStore,
                AppSettingsStore
            );

        const history = useHistory();

        const handleClose = () => {
            navigateToClose();
        };

        let myMessage = 0;
        let botMessage = 0;

        useEffect(() => {
            const fetchFmaAppVersion = async () => {
                const meta = await WFApp.metaData();
                setFmaAppVersion(meta?.appVersion ?? '0.0.0');
            };
            if (!openedFromCordova) {
                fetchFmaAppVersion();
            }
        }, [openedFromCordova, setFmaAppVersion]);

        const handleHasEquipmentConnection = useCallback(
            (hasConnection: boolean) => {
                chatStore.enqueueMessages([
                    {
                        text: hasConnection ? 'Yes' : 'No',
                        type: MessageType.Text,
                        isMine: true,
                        date: new Date(),
                    },
                ]);

                if (hasConnection) {
                    setTimeout(() => {
                        history.push(
                            `/${mainStore.selectedAppointment?.id}/${mainStore.selectedTopic?.id}/equipment-picker`
                        );
                    }, 500);
                }
            },
            [chatStore, history, mainStore]
        );

        const handleTopicClick = useCallback(
            (topic: TopicViewModel) => {
                if (mainStore.selectedTopic) {
                    chatStore.filterOutRemovableMessages();
                }
                mainStore.setSelectedTopic(topic);

                chatStore.enqueueMessages([
                    {
                        text: topic.name,
                        type: MessageType.Text,
                        isMine: true,
                        removable: true,
                        date: new Date(),
                    },
                ]);

                switch (topic.type) {
                    case TopicType.InformationInServiceTitan:
                        // skip the question and go to the next step
                        setTimeout(() => {
                            history.push(
                                `/${mainStore.selectedAppointment?.id}/${topic.id}/questions`
                            );
                        }, 500);
                        break;
                    case TopicType.Troubleshooting:
                    case TopicType.EquipmentInformation:
                        setTimeout(() => {
                            /*
                             * if (mainStore.selectedEquipmentInfo) {
                             *     history.push(
                             *         `/${mainStore.selectedJob?.id}/${topic.id}/${mainStore.selectedMappedEquipments?.map(x => x.id)?.join(',')}/questions`
                             *     );
                             * } else {
                             *
                             * }
                             */
                            history.push(
                                `/${mainStore.selectedAppointment?.id}/${topic.id}/equipment-picker`
                            );
                        }, 500);
                        break;
                    case TopicType.FieldProCoaching:
                        // Deep-link into the isolated field-pro agent route (job entry context).
                        setTimeout(() => {
                            history.push(
                                `/topic/field-pro/job/${mainStore.selectedAppointment?.id}/questions?fromInApp=1`
                            );
                        }, 500);
                        break;
                }

                if (topic.message) {
                    chatStore.enqueueMessages([
                        {
                            text: topic.message,
                            type: MessageType.Text,
                            isMine: false,
                            removable: true,
                        },
                        {
                            type: MessageType.TopicYesNo,
                            isMine: false,
                            removable: true,
                            date: new Date(),
                        },
                    ]);
                }
            },
            [chatStore, history, mainStore]
        );

        const handleTopicButtonClick = useCallback(
            (data: string) => {
                if (data === 'ReplacementPartFinder') {
                    history.push(`/${mainStore.selectedAppointment?.id}/replacement-part-finder`);
                } else if (data === 'CalculatorsDiagnosticTests') {
                    history.push(`/${mainStore.selectedAppointment?.id}/calculator`);
                }
            },
            [history, mainStore.selectedAppointment?.id]
        );

        const renderMessage = useCallback(
            (message: IMessage, key: string, showAvatar: boolean, tenantName = 'tenant name') => {
                switch (message.type) {
                    case MessageType.Text:
                        return (
                            <MessageRow
                                key={key}
                                isMine={message.isMine}
                                showAvatar={showAvatar}
                                name={tenantName}
                                date={message.date}
                            >
                                <MessageBubble isMine={message.isMine}>
                                    <Text>{message.text}</Text>
                                </MessageBubble>
                            </MessageRow>
                        );
                    case MessageType.RichText:
                        return (
                            <MessageRow
                                key={key}
                                isMine={message.isMine}
                                showAvatar={showAvatar}
                                name={tenantName}
                                date={message.date}
                            >
                                <MessageBubble isMine={message.isMine}>
                                    <Text>
                                        {/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */}
                                        <span
                                            key={key}
                                            dangerouslySetInnerHTML={{ __html: message.text ?? '' }}
                                        />
                                        {/* eslint-enable react/no-danger,@typescript-eslint/naming-convention */}
                                    </Text>
                                </MessageBubble>
                            </MessageRow>
                        );
                    case MessageType.Topic:
                        return (
                            <MessageRow key={key} isMine={message.isMine} date={message.date}>
                                <TopicsViews
                                    topics={message.data as TopicViewModel[]}
                                    onTopicClick={handleTopicClick}
                                />
                            </MessageRow>
                        );
                    case MessageType.TopicYesNo:
                        return (
                            <MessageRow
                                key={key}
                                date={message.date}
                                isMine={message.isMine}
                                showAvatar={false}
                            >
                                <YesNoButton
                                    onYes={() => handleHasEquipmentConnection(true)}
                                    onNo={() => handleHasEquipmentConnection(false)}
                                />
                            </MessageRow>
                        );
                    case MessageType.Button:
                        return (
                            <MessageRow
                                key={key}
                                date={message.date}
                                isMine={message.isMine}
                                showAvatar={false}
                            >
                                <Button
                                    appearance="secondary"
                                    size="small"
                                    onClick={() => handleTopicButtonClick(message.data as string)}
                                >
                                    {message.text}
                                </Button>
                            </MessageRow>
                        );

                    case MessageType.Error:
                        return (
                            <MessageRow key={key} isMine={message.isMine} date={message.date}>
                                <ErrorView data={message.data as ErrorModel} />
                            </MessageRow>
                        );
                    default:
                        return null;
                }
            },
            [handleHasEquipmentConnection, handleTopicButtonClick, handleTopicClick]
        );

        return (
            <ChatLayout
                subHeader={
                    <TopicPickerSubHeader coachingLive={topicPickerStore.fieldProCoachingLive} />
                }
                header={<ChatHeaderNew onNavigationButtonClick={handleClose} icon="close" />}
            >
                {chatStore.messages.map((message, index) => {
                    if (message.isMine) {
                        myMessage++;
                        botMessage = 0;
                    } else {
                        botMessage++;
                        myMessage = 0;
                    }
                    return renderMessage(
                        message,
                        index.toString(),
                        myMessage === 1 || botMessage === 1
                    );
                })}
            </ChatLayout>
        );
    })
);
